/* eslint-disable react/no-unescaped-entities */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Play, Newspaper } from "lucide-react";

const VIDEOS = [
    {
        type: "mp4",
        id: "workshop-facilitating-change",
        src: "https://dl.dropboxusercontent.com/scl/fi/q7twpcok09azocm86gfyw/WORKSHOP-2-FA.mp4?rlkey=w3cfugr2xnxylhkpjm7a49pv1&st=0epf6dp2&dl=0",
        title: "Workshop · Facilitating Change",
        description: "Reico Hopewell leading a community workshop — equipping participants with the tools and language to build their next chapter.",
    },
    {
        type: "youtube",
        id: "kT2uVMMv7rc",
        title: "Reico Hopewell — Feature",
        description: "Watch Reico share his work, mission, and the impact of the Hopewell ventures.",
    },
    {
        type: "youtube",
        id: "RTTTvUWRj1E",
        title: "Community Conversation",
        description: "A conversation on recovery, leadership, and building lasting community impact.",
    },
    {
        type: "youtube",
        id: "3XzsjM5yvi8",
        title: "Behind the Mission",
        description: "Inside the work of The HopeWell Foundation and The Mend House.",
    },
    {
        type: "youtube",
        id: "fMYL8PTI3V8",
        title: "Leadership in Action",
        description: "Reico on entrepreneurship, faith, and turning vision into reality.",
    },
];

const CLIPS = [
    {
        src: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/fuinwfnm_VID_20260613_212909.mp4",
        title: "On the Ground · Clip 1",
        description: "Direct from Reico — captured live from his work in the community.",
    },
    {
        src: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/3qggg7kw_VID_20260613_212912.mp4",
        title: "On the Ground · Clip 2",
        description: "A second look at the work in motion, straight from the source.",
    },
];

const ARTICLES = [
    {
        title: "Knoxville's Reico Hopewell — Transformed After a Life of Addiction",
        outlet: "Knox News",
        date: "December 22, 2022",
        url: "https://www.knoxnews.com/story/news/health/2022/12/22/knoxville-reico-hopewell-transformed-after-life-addiction/69731318007",
        excerpt: "An in-depth feature on Reico's journey from adversity to building one of East Tennessee's most respected recovery and community organizations.",
    },
];

function VideoCard({ video, onPlay, isPlaying }) {
    const isMp4 = video.type === "mp4";

    return (
        <article
            data-testid={`video-${video.id}`}
            className="exec-card overflow-hidden flex flex-col"
        >
            <div className="relative aspect-video bg-[#0A192F] overflow-hidden">
                {isMp4 ? (
                    <video
                        controls
                        preload="metadata"
                        playsInline
                        className="w-full h-full object-contain bg-black"
                    >
                        <source src={video.src} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                ) : isPlaying ? (
                    <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                ) : (
                    <button
                        type="button"
                        onClick={onPlay}
                        data-testid={`video-play-${video.id}`}
                        className="group w-full h-full relative cursor-pointer"
                        aria-label={`Play ${video.title}`}
                    >
                        <img
                            src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                            onError={(e) => {
                                e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                            }}
                            alt={video.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                            <div className="w-20 h-20 rounded-full bg-[#D4AF37] text-[#0A192F] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                                <Play className="w-8 h-8 ml-1" fill="currentColor" />
                            </div>
                        </div>
                    </button>
                )}
            </div>
            <div className="p-6">
                <h3 className="font-serif text-xl font-bold text-[#0A192F] leading-snug">
                    {video.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {video.description}
                </p>
            </div>
        </article>
    );
}

export default function Media() {
    const [playingId, setPlayingId] = useState(null);

    return (
        <div data-testid="media-page">
            {/* Hero */}
            <section className="bg-[#0A192F] text-white py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <span className="overline">Videos · Press · Features</span>
                    <h1 className="font-serif text-5xl sm:text-6xl font-bold mt-4 leading-tight max-w-4xl">
                        See the work{" "}
                        <span className="italic text-[#D4AF37]">in motion.</span>
                    </h1>
                    <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">
                        Videos, interviews, and press features documenting
                        Reico Hopewell's leadership, the Hopewell ventures, and
                        the impact across Knoxville and East Tennessee.
                    </p>
                </div>
            </section>

            {/* Videos grid */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="mb-12 flex items-baseline gap-4">
                        <span className="num-tag">01</span>
                        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0A192F]">
                            Featured Videos
                        </h2>
                        <span className="gold-rule flex-1 max-w-[60px]" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                        {VIDEOS.map((v) => (
                            <VideoCard
                                key={v.id}
                                video={v}
                                isPlaying={playingId === v.id}
                                onPlay={() => setPlayingId(v.id)}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Self-hosted clips */}
            <section className="py-24 border-t border-slate-200">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="mb-12 flex items-baseline gap-4">
                        <span className="num-tag">02</span>
                        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0A192F]">
                            On the Ground
                        </h2>
                        <span className="gold-rule flex-1 max-w-[60px]" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                        {CLIPS.map((c) => (
                            <article
                                key={c.src}
                                data-testid={`clip-${c.title.replace(/\s+/g, "-").toLowerCase()}`}
                                className="exec-card overflow-hidden flex flex-col"
                            >
                                <div className="aspect-video bg-[#0A192F]">
                                    <video
                                        controls
                                        preload="metadata"
                                        playsInline
                                        className="w-full h-full object-contain bg-black"
                                    >
                                        <source src={c.src} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                                <div className="p-6">
                                    <h3 className="font-serif text-xl font-bold text-[#0A192F] leading-snug">
                                        {c.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                        {c.description}
                                    </p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Press / articles */}
            <section className="bg-slate-50 py-24 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="mb-12 flex items-baseline gap-4">
                        <span className="num-tag">03</span>
                        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0A192F]">
                            In the Press
                        </h2>
                        <span className="gold-rule flex-1 max-w-[60px]" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                        {ARTICLES.map((a) => (
                            <a
                                key={a.url}
                                href={a.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`article-${a.outlet.replace(/\s+/g, "-").toLowerCase()}`}
                                className="exec-card p-8 group block"
                            >
                                <div className="flex items-center gap-3 mb-5">
                                    <Newspaper className="w-5 h-5 text-[#D4AF37]" />
                                    <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0A192F]">
                                        {a.outlet} · {a.date}
                                    </span>
                                </div>
                                <h3 className="font-serif text-2xl font-bold text-[#0A192F] leading-snug">
                                    {a.title}
                                </h3>
                                <p className="mt-4 text-slate-600 leading-relaxed">
                                    {a.excerpt}
                                </p>
                                <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#0A192F] group-hover:text-[#D4AF37] transition-colors">
                                    Read article <ExternalLink className="w-3.5 h-3.5" />
                                </span>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0A192F] text-white py-20">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <h3 className="font-serif text-3xl sm:text-4xl font-bold leading-tight">
                        Inspired? Let's talk about your project.
                    </h3>
                    <Link
                        to="/contact"
                        data-testid="media-cta"
                        className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] hover:bg-[#C5A059] rounded-sm px-8 py-4 font-bold text-sm uppercase tracking-widest"
                    >
                        Schedule a Consultation <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
